import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../hooks/redux'
import { CustomIcon } from '../../components/customIcon/customIcon'
import { CustomButton } from '../../components/customButton'
import { VoiceCard } from './voiceCard'
import { GroupAccessModal } from './groupAccessModal'
import { AddVoiceModal } from './addVoiceModal'
import { StandConfigModal } from './standConfigModal'
import { Skeleton } from '../../components/skeleton'
import { getVoiceGroups, deleteVoiceGroup, updateVoiceGroup } from '../../store/slices/voiceGroups/async'
import { getVoices } from '../../store/slices/tts/async'
import { getUsers } from '../../store/slices/user/async'
import { IVoiceGroup, IVoiceGroupUser } from '../../store/slices/voiceGroups/voiceGroupsSlice'
import { pluralVoices } from '../../functions'
import classes from '../../styles/voices/groupsTab.module.scss'
import cn from 'classnames'


type TGroupIcon = 'crown' | 'ttsLogo' | undefined

export type TGroup = {
  name: string
  value: string
  count: number
  icon?: TGroupIcon
  required?: boolean
  users: IVoiceGroupUser[]
}

const toTGroup = (g: IVoiceGroup): TGroup => ({
  name: g.name,
  value: String(g.voice_group_id),
  count: g.voices.length,
  icon: g.voice_group_id === 69 ? 'crown' : g.is_system ? 'ttsLogo' : undefined,
  required: g.is_system,
  users: g.users,
})

export const GroupsTab = () => {
  const dispatch = useDispatch()
  const { groups, loading } = useSelector((state) => state.voiceGroups)
  const ttsVoices = useSelector((state) => state.tts.voices)
  const [selected, setSelected] = useState<string | null>(null)
  const [accessModal, setAccessModal] = useState<TGroup | null>(null)
  const [addVoiceModal, setAddVoiceModal] = useState(false)
  const [standConfigOpen, setStandConfigOpen] = useState(false)
  const [selectedVoices, setSelectedVoices] = useState<number[]>([])
  const authToken = useSelector((state) => state.user.user.auth_token)
  const users = useSelector((state) => state.user.users)

  const toggleVoice = (id: number) => {
    if (selected === '69') return
    setSelectedVoices((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id])
  }

  useEffect(() => {
    dispatch(getVoiceGroups() as any)
    if (users.length === 0) dispatch(getUsers(authToken) as any)
    if (ttsVoices.length === 0) dispatch(getVoices() as any)
  }, [dispatch])

  const handleSelectGroup = (value: string) => {
    setSelected(value)
    setSelectedVoices([])
  }

  const requiredGroups = groups.filter((g) => g.is_system).map(toTGroup).reverse()
  const customGroups = groups.filter((g) => !g.is_system).map(toTGroup)
  const selectedGroup = [...requiredGroups, ...customGroups].find((g) => g.value === selected)

  return (
    <div className={classes.wrapper}>
      {accessModal && (
        <GroupAccessModal group={accessModal} onClose={() => setAccessModal(null)} />
      )}
      {addVoiceModal && selectedGroup && (
        <AddVoiceModal group={selectedGroup} onClose={() => setAddVoiceModal(false)} />
      )}
      {standConfigOpen && <StandConfigModal onClose={() => setStandConfigOpen(false)} />}

      <div className={classes.list}>
        {loading && groups.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Skeleton type="list-item" />
            <Skeleton type="list-item" />
          </div>
        )}
        {requiredGroups.map((group, i) => (
          <React.Fragment key={group.value}>
            <div
              className={cn(classes.item, selected === group.value && classes.itemActive)}
              onClick={() => handleSelectGroup(group.value)}
            >
              <div className={classes.itemLeft}>
                <div className={classes.itemInfo}>
                  <div className={classes.itemNameRow}>
                    {group.icon && (
                      <span className={group.icon === 'crown' ? classes.iconSmall : classes.iconMedium}>
                        <CustomIcon icon={group.icon} />
                      </span>
                    )}
                    <span className={cn(classes.itemName, classes.itemNameRequired)}>{group.name}</span>
                  </div>
                  <span className={classes.itemCount}>{pluralVoices(group.count)}</span>
                </div>
              </div>
              <div className={classes.itemActions}>
                <span className={classes.actionIcon} onClick={(e) => { e.stopPropagation(); setAccessModal(group) }}>
                  <CustomIcon icon="userIcon" />
                </span>
              </div>
            </div>
            <div className={classes.divider} />
          </React.Fragment>
        ))}

        {customGroups.map((group, i) => (
          <React.Fragment key={group.value}>
            <div
              className={cn(classes.item, selected === group.value && classes.itemActive)}
              onClick={() => handleSelectGroup(group.value)}
            >
              <div className={classes.itemLeft}>
                <div className={classes.itemInfo}>
                  <span className={classes.itemName}>{group.name}</span>
                  <span className={classes.itemCount}>{pluralVoices(group.count)}</span>
                </div>
              </div>
              <div className={classes.itemActions}>
                <span className={classes.actionIcon} onClick={(e) => { e.stopPropagation(); setAccessModal(group) }}>
                  <CustomIcon icon="userIcon" />
                </span>
                <span className={cn(classes.actionIcon, classes.actionIconDelete)} onClick={(e) => { e.stopPropagation(); dispatch(deleteVoiceGroup(Number(group.value)) as any) }}>
                  <CustomIcon icon="delete" />
                </span>
              </div>
            </div>
            {i < customGroups.length - 1 && <div className={classes.divider} />}
          </React.Fragment>
        ))}
      </div>

      <div className={classes.detail}>
        {selectedGroup ? (
          <>
            {(() => {
              const voices = groups.find((g) => String(g.voice_group_id) === selected)?.voices ?? []
              return voices.length === 0 ? (
                <div className={classes.detailGridEmpty}>
                  <CustomIcon icon="groupIcon" />
                  <span>В этой группе пока нет голосов</span>
                </div>
              ) : (
                <div className={classes.detailGrid}>
                  {voices.map((voice, i) => {
                    const ttsVoice = ttsVoices.find((v) => v.name === voice.technical_name)
                    return (
                      <VoiceCard
                        key={i}
                        name={ttsVoice?.title || voice.technical_name}
                        gender={ttsVoice?.sex || undefined}
                        lang={ttsVoice?.languages?.[0]}
                        avatar={ttsVoice?.avatar || undefined}
                        previewAudio={ttsVoice?.previewAudio || undefined}
                        className={classes.groupCard}
                        selected={selectedVoices.includes(voice.voice_id)}
                        onClick={() => toggleVoice(voice.voice_id)}
                      />
                    )
                  })}
                </div>
              )
            })()}
            <div className={classes.detailBar}>
              <div className={classes.detailBarLeft}>
                <CustomButton className={classes.detailBarAdd} onClick={() => setAddVoiceModal(true)}>Добавить голос</CustomButton>
                {/* {selectedGroup?.value === 'default' && (
                  <CustomButton className={classes.detailBarAdd} onClick={() => setStandConfigOpen(true)}>
                    Настроить стенд
                  </CustomButton>
                )} */}
              </div>
              {selectedGroup?.value !== 'all' && selected !== '69' && selectedVoices.length > 0 && (
                <CustomButton
                  className={classes.detailBarRemove}
                  onClick={() => {
                    const group = groups.find((g) => String(g.voice_group_id) === selected)
                    if (!group) return
                    const remaining = group.voices
                      .filter((v) => !selectedVoices.includes(v.voice_id))
                      .map((v) => v.technical_name)
                    dispatch(updateVoiceGroup({ voice_group_id: group.voice_group_id, voices: remaining }) as any)
                    setSelectedVoices([])
                  }}
                >
                  Убрать из группы {selectedVoices.length > 0 ? `(${selectedVoices.length})` : ''}
                </CustomButton>
              )}
            </div>
          </>
        ) : (
          <div className={classes.empty}>
            <CustomIcon icon="groupIcon" />
            <span>Выберите группу для<br />просмотра и редактирования</span>
          </div>
        )}
      </div>
    </div>
  )
}
